<template>
  <div class="bg-gradient-black-01 w-full rounded-3xl px-6 py-8 lg:max-w-sm">
    <div
      v-if="division || employmentType || workTypes?.length"
      class="flex flex-wrap gap-2 pb-4">
      <NuxtLink
        v-if="division"
        :href="`${jobsPagePath}?field=${getDivisionKey(division)}`"
        class="flex-shrink-0 rounded-full border border-gray-100 bg-gray-100 px-4 py-1 text-sm text-gray-600">
        {{ division }}
      </NuxtLink>
      <NuxtLink
        v-if="employmentType"
        :href="`${jobsPagePath}?type=${getEmploymentTypeKey(employmentType)}`"
        class="flex-shrink-0 rounded-full border border-gray-100 px-4 py-1 text-sm text-gray-100">
        {{ employmentType }}
      </NuxtLink>
      <template v-if="workTypes">
        <NuxtLink
          v-for="workType in workTypes"
          :key="workType"
          :href="`${jobsPagePath}?workType=${getWorkTypeKey(workType)}`"
          class="flex-shrink-0 rounded-full border border-gray-100 px-4 py-1 text-sm text-gray-100">
          {{ workType }}
        </NuxtLink>
      </template>
    </div>
    <div
      v-if="content.locations?.items?.length"
      class="border-t border-gray-300 py-4 first:border-0 md:max-w-[60%] lg:max-w-full">
      <h3 class="mb-2 text-sm font-semibold uppercase tracking-wider text-white">
        {{ content.locations.title }}
      </h3>
      <div
        v-for="location in content.locations.items"
        :key="location.key"
        class="flex py-2.5 text-sm">
        <component
          :is="`Icon${getLocationIconName(location.label)}`"
          v-if="location.key !== '/'"
          class="mx-2.5 h-6 w-6" />
        {{ location.label }}
      </div>
    </div>
    <div
      v-if="recruiter"
      class="border-t border-gray-300 py-4 first:border-0 md:max-w-[60%] lg:max-w-full">
      <h3 class="text-sm font-semibold uppercase tracking-wider text-white">
        {{ content.recruiter.title }}
      </h3>
      <div class="mt-4 flex items-center">
        <NuxtImg
          :src="recruiter.image"
          :alt="recruiter.title || ''"
          class="mr-4 h-14 w-14 flex-shrink-0 rounded-full object-cover grayscale"
          sizes="sm:56px" />
        <div class="">
          <p class="text-gray-100">
            {{ recruiter.title }}
          </p>
          <p class="font-normal text-gray-200">
            {{ recruiter.position }}
          </p>
        </div>
      </div>
      <a
        :href="`mailto:${recruiter.email}`"
        class="bottom-line relative mt-2 inline-flex items-center text-gray-100">
        <IconMail class="mr-2 h-5 w-5 fill-current" />
        {{ recruiter.email }}
      </a>
    </div>
    <div
      v-if="content.employers?.items?.length"
      class="border-t border-gray-300 py-4 first:border-0 md:max-w-[60%] lg:max-w-full">
      <h3 class="text-sm font-semibold uppercase tracking-wider text-white">
        {{ content.employers.title }}
      </h3>
      <p
        v-for="employer in content.employers.items"
        :key="employer"
        class="mt-2 font-normal text-gray-200">
        {{ employer }}
      </p>
    </div>
    <div
      v-if="content.deadline?.copy"
      class="border-t border-gray-300 py-4 first:border-0 md:max-w-[60%] lg:max-w-full">
      <h3 class="text-sm font-semibold uppercase tracking-wider text-white">
        {{ content.deadline.title }}
      </h3>
      <p class="mt-2 font-normal text-gray-200">
        {{
          new Date(content.deadline.copy).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        }}
      </p>
    </div>

    <ButtonBase
      :label="cta"
      type="filled"
      class="ml-auto mt-2 w-full md:w-auto lg:w-full"
      @click="onButtonClick" />
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { queryContent, useAsyncData } from '#imports'

  import {
    type DivisionLabel,
    DIVISIONS,
    type Employer,
    EMPLOYMENT_TYPES,
    type EmploymentTypeLabel,
    WORK_TYPES,
    type WorkTypeLabel
  } from '~/constants/jobs'
  import type { JobLocation } from '~/models/jobs'

  type sidebarProps = {
    division?: DivisionLabel
    employmentType?: EmploymentTypeLabel
    workTypes?: WorkTypeLabel[]
    content: {
      locations?: { title: string; items: JobLocation[] }
      recruiter?: { title: string; id: string }
      employers?: { title: string; items: Employer[] }
      deadline?: { title: string; copy: string | null }
    }
    cta: string
  }

  type Recruiter = {
    title?: string
    image: string
    position: string
    email: string
  }

  const props = defineProps<sidebarProps>()

  const emit = defineEmits(['buttonClick'])

  const recruiterId = props.content.recruiter?.id
  const recruiter = ref<Recruiter | null>(null)

  if (recruiterId) {
    const { data } = await useAsyncData(recruiterId, () =>
      queryContent<Recruiter>('recruiters')
        .where({ title: { $eq: recruiterId } })
        .findOne()
    )

    recruiter.value = data.value
  }

  const { isInternalJobReferrals } = useInternalJobreferrals()

  const jobsPagePath = isInternalJobReferrals.value ? '/internal-job-referrals' : '/careers/jobs'

  function getDivisionKey(divisionLabel: DivisionLabel) {
    return DIVISIONS.find(division => division?.label === divisionLabel)?.key || ''
  }

  function getEmploymentTypeKey(employmentTypeLabel: EmploymentTypeLabel) {
    return EMPLOYMENT_TYPES.find(employmentType => employmentType.label === employmentTypeLabel)?.key || ''
  }

  function getWorkTypeKey(workTypeLabel: WorkTypeLabel) {
    return WORK_TYPES.find(workType => workType?.label === workTypeLabel)?.key || ''
  }

  function onButtonClick() {
    emit('buttonClick')
  }

  function getLocationIconName(location: string) {
    return location.split(',')[1].trim().replace(' ', '')
  }
</script>
